'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getSocket } from '@/lib/echo';

import {
  ocrKeys,
  resetPageSuggestionPollWindow
} from '../services/ocr.queries';

interface OcrPageTemplateSuggestionsReadyEvent {
  container_id: number;
}

/**
 * Push-first delivery for per-page template suggestions. The backend event
 * carries no payload (the suggestion response holds signed image urls that
 * would expire over the socket), so on receipt we invalidate the query and
 * refetch — re-hydrating with fresh urls. The query's polling stays on as a
 * slow safety net so a missed broadcast still resolves.
 */
export function usePageTemplateSuggestionsLive(
  containerId: number,
  enabled: boolean
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || containerId <= 0) return;

    let cancelled = false;
    const channelName = `ocr-page-suggestions.${containerId}`;

    getSocket().then((socket) => {
      if (cancelled) return;

      socket
        .private(channelName)
        .listen(
          '.ocr-page-template-suggestions',
          (_event: OcrPageTemplateSuggestionsReadyEvent) => {
            resetPageSuggestionPollWindow(containerId);
            queryClient.invalidateQueries({
              queryKey: ocrKeys.pageTemplateSuggestions(containerId)
            });
          }
        );
    });

    return () => {
      cancelled = true;
      getSocket().then((socket) => socket.leave(channelName));
    };
  }, [enabled, containerId, queryClient]);
}
